import { FC, ReactNode } from 'react';

import { SwipableBox } from '@eneco/custom-components/SwipableBox/SwipableBox';
import { useLinkComponent } from '@eneco/link';
import { Box, Divider, NavLink, PageGrid, Stack, Text } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronRightIcon, HomeIcon } from '@eneco/sparky/icons';
import { BreadCrumbsRendering } from '@sitecore/types/manual/BreadCrumbs';
import { BreadcrumbList, WithContext } from 'schema-dts';

const ConditionalWrapper: FC<{ children: ReactNode; shouldWrap: boolean }> = ({ shouldWrap, children }) => {
  return shouldWrap ? (
    <PageGrid>
      <PageGrid.Item gridColumn="1/-1">
        <Divider />
        {children}
      </PageGrid.Item>
    </PageGrid>
  ) : (
    <>{children}</>
  );
};

const origin = typeof window !== 'undefined' ? window.location.origin : '';

function toAbsoluteUrl(url: string): string {
  if (/^https?:\/\//.test(url)) return url;
  return `${origin}${url.startsWith('/') ? url : `/${url}`}`;
}

const BreadCrumbs: FC<BreadCrumbsRendering> = ({ fields }) => {
  const Link = useLinkComponent();

  const isDesktop = useMediaQuery('lg');
  const paddingX = isDesktop ? '0' : '3';

  if (!fields.breadCrumbs?.length) return null;

  const jsonLd: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: fields.breadCrumbs.map((breadCrumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@id': toAbsoluteUrl(breadCrumb.url),
        name: breadCrumb.name,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
      />
      <ConditionalWrapper shouldWrap={isDesktop}>
        <SwipableBox as="nav" paddingY="3" paddingLeft={paddingX}>
          <Stack direction="row" alignY="center" as="ul" gap="1">
            {fields.breadCrumbs.map((breadCrumb, i) => (
              <Text as="li" key={i} whiteSpace="nowrap">
                <Stack direction="row" gap="1">
                  {i === fields.breadCrumbs.length - 1 ? (
                    <Box paddingRight={paddingX}>
                      <Text size="BodyS" color="textLowEmphasis">
                        {breadCrumb.name}
                      </Text>
                    </Box>
                  ) : (
                    <>
                      <Link href={breadCrumb.url} linkType={breadCrumb.linktype} currentMatch="exact">
                        <NavLink variant="tertiary" leftIcon={i === 0 ? <HomeIcon /> : undefined}>
                          {breadCrumb.name}
                        </NavLink>
                      </Link>
                      <ChevronRightIcon color="iconSecondary" />
                    </>
                  )}
                </Stack>
              </Text>
            ))}
          </Stack>
        </SwipableBox>
      </ConditionalWrapper>
    </>
  );
};

export { BreadCrumbs };
export default BreadCrumbs;
